import { message } from 'antd';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { PrintableEpcEstimate } from '@/components/epc/estimation/PrintableEpcEstimate';
import { openPmvPrintOverlay } from '@/utils/openPmvPrintOverlay';
import type { EpcEstimatePrintBundle } from '@/utils/epcEstimatePrint';

function asRows(v: unknown): any[] {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object' && Array.isArray((v as any).items)) return (v as any).items;
  return [];
}

/** Fetch full EST3 revision commercial detail and open landscape print preview. */
export async function openEpcEstimatePrint(opts: {
  estimateId: string;
  revisionId: string;
  tenderTitle?: string;
}): Promise<void> {
  const { estimateId, revisionId, tenderTitle } = opts;
  const hide = message.loading('Preparing estimate print…', 0);
  try {
    const [ws, boq, costs, indirects, contingency, duration, total, pricing] = await Promise.all([
      epcEstimationService.getWorkspace(estimateId),
      epcEstimationService.listClientBoq(estimateId, revisionId, { limit: 5000, offset: 0 }),
      epcEstimationService.listCostLines(estimateId, revisionId, { limit: 5000, offset: 0 }),
      epcEstimationService.listIndirectCosts(estimateId, revisionId),
      epcEstimationService.getContingency(estimateId, revisionId).catch(() => null),
      epcEstimationService.getDuration(estimateId, revisionId).catch(() => null),
      epcEstimationService.totalCostSummary(estimateId, revisionId).catch(() => null),
      epcEstimationService.getPricing(estimateId, revisionId).catch(() => null),
    ]);

    const estimate = (ws as any)?.estimate ?? ws ?? {};
    const revision =
      (ws as any)?.revisions?.find((r: any) => String(r.id) === String(revisionId)) ||
      (ws as any)?.currentRevision ||
      {};
    const currency = String(
      estimate.base_currency || revision.currency || total?.currency || 'AED'
    );

    const bundle: EpcEstimatePrintBundle = {
      estimate,
      revision,
      currency,
      tenderTitle,
      boq: asRows(boq),
      costs: asRows(costs),
      indirects: asRows(indirects),
      contingency: contingency && typeof contingency === 'object' ? contingency : null,
      duration: duration && typeof duration === 'object' ? duration : null,
      total: total && typeof total === 'object' ? total : null,
      pricing: pricing && typeof pricing === 'object' ? pricing : null,
    };

    // Hide #root (not just content) so layout footer/chrome cannot leave a blank band.
    openPmvPrintOverlay((onClose) => (
      <PrintableEpcEstimate bundle={bundle} onClose={onClose} />
    ));
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    message.error(err?.response?.data?.message || err?.message || 'Failed to open estimate print');
  } finally {
    hide();
  }
}
